



































































































import { Component, Vue } from "vue-property-decorator";
import router from "@/router/router";
import DeviceReportJsonTreeComponent from "@/components/admin/deviceReports/DeviceReportJsonTree.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import {
  formatDateToHoursAndMinuteString,
  formatDateTohDateMonthYearString,
} from "@/utils/DateHelper";
import {
  DisplayStep,
  getOverallResult,
  isPassResult,
  toDisplaySteps,
} from "@/utils/DeviceReportDisplay";
import {
  DeviceReportPdfLabels,
  downloadDeviceReportJson,
  downloadDeviceReportPdf,
} from "@/utils/DeviceReportDownload";
import {
  DOWNLOAD_ICON,
  DownloadFileType,
  getFileTypeIcon,
} from "@/utils/FileTypeIcons";
import { getDeviceReportContent } from "@/data/repository/DeviceReportRepository";
import { parseReportId } from "@/data/models/DeviceReport";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { themed } from "@/utils/Theme";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";

/**
 * Shows one device report.
 *
 * The report is addressed by its Cloud Storage location, passed as a query parameter, so the same
 * screen serves a report attached to a device and one still waiting in the unassigned area — those
 * have no Firestore document at all, so there is no id to address them by.
 *
 * Nothing here trusts the payload's shape beyond `steps`, and even that is rendered only when it is
 * an array of objects. Everything else goes through the generic tree. The shaping lives in
 * `DeviceReportDisplay`, where it is unit-tested against a real capture.
 */

@Component({
  components: { "device-report-json-tree": DeviceReportJsonTreeComponent, "content-state": ContentStateComponent },
})
export default class DeviceReportViewerComponent extends Vue {
  report: unknown = null;
  isLoading = true;
  loadError = false;
  /** PDF first: it is what gets attached to a ticket or a mail. */
  selectedFormat: DownloadFileType = DownloadFileType.PDF;

  get storageLocation(): string {
    return (this.$route.query.location as string) ?? "";
  }

  /** Serial or MAC, whichever the caller knew. Display only. */
  get subject(): string {
    return (this.$route.query.subject as string) ?? "";
  }

  get reportId(): string {
    return (this.$route.query.reportId as string) ?? "";
  }

  get subjectLabel(): string {
    return this.subject || this.$t("admin.deviceReports.viewer.unknownDevice").toString();
  }

  /**
   * When the device was tested, from the report id.
   *
   * The id is the report's identity and encodes the capture time in UTC, so this needs no extra
   * read and works for an unassigned report that has no document. Rendered in local time.
   */
  get capturedAtLabel(): string {
    const parsed = parseReportId(this.reportId);

    if (!parsed) {
      return "";
    }

    return `${formatDateTohDateMonthYearString(parsed.reportedAt)} ` +
      `${formatDateToHoursAndMinuteString(parsed.reportedAt)}`;
  }

  get overallResult(): string {
    return getOverallResult(this.report);
  }

  get resultColor(): string {
    return this.statusColor(this.overallResult);
  }

  get steps(): DisplayStep[] {
    return toDisplaySteps(this.report);
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** Nothing to save until the payload is there. */
  get canDownload(): boolean {
    return !this.isLoading && !this.loadError && this.report !== null;
  }

  /**
   * The two formats, each with its own file-type icon.
   *
   * PDF is the readable rendering; JSON is what the production rig uploaded, so it can be diffed
   * against the file on the rig or attached to a firmware ticket.
   */
  get formatOptions() {
    return [
      {
        label: this.$t("admin.deviceReports.viewer.formatPdf").toString(),
        value: DownloadFileType.PDF,
        icon: getFileTypeIcon(DownloadFileType.PDF),
      },
      {
        label: this.$t("admin.deviceReports.viewer.formatJson").toString(),
        value: DownloadFileType.JSON,
        icon: getFileTypeIcon(DownloadFileType.JSON),
      },
    ];
  }

  get downloadIcon(): string {
    return DOWNLOAD_ICON;
  }

  get pdfLabels(): DeviceReportPdfLabels {
    return {
      title: this.$t("admin.deviceReports.viewer.title").toString(),
      steps: this.$t("admin.deviceReports.viewer.steps").toString(),
      step: this.$t("admin.deviceReports.viewer.step").toString(),
      comment: this.$t("admin.deviceReports.viewer.comment").toString(),
      status: this.$t("admin.deviceReports.viewer.status").toString(),
      detail: this.$t("admin.deviceReports.viewer.detail").toString(),
      duration: this.$t("admin.deviceReports.viewer.duration").toString(),
      rawData: this.$t("admin.deviceReports.viewer.rawData").toString(),
    };
  }

  statusColor(status: string): string {
    return isPassResult(status) ? "success" : "error";
  }

  onDownload() {
    try {
      if (this.selectedFormat === DownloadFileType.JSON) {
        downloadDeviceReportJson(this.report, this.subject, this.reportId);
        return;
      }

      downloadDeviceReportPdf(this.report, {
        subject: this.subject,
        subjectLabel: this.subjectLabel,
        capturedAtLabel: this.capturedAtLabel,
        overallResult: this.overallResult,
        reportId: this.reportId,
        labels: this.pdfLabels,
      });
    } catch {
      // pdfmake renders in the browser, so a payload it cannot lay out fails here rather than
      // server-side. Reporting it beats a button that silently does nothing; the other format may
      // well still work.
      SnackbarModule.showError({ key: "admin.deviceReports.viewer.downloadFailed" });
    }
  }

  /**
   * The viewer opens only from the results, whose filters are in their URL; going back in the
   * history returns to exactly that list, which a named route would lose.
   */
  onBack() {
    router.back();
  }

  async mounted() {
    // "Testberichte > BS1234": the device is named in the app bar only.
    ToolbarContentModule.setToolbarTitle(`${this.$t("admin.deviceReports.title")} > ${this.subjectLabel}`);

    if (!this.storageLocation) {
      this.loadError = true;
      this.isLoading = false;
      return;
    }

    try {
      this.report = await getDeviceReportContent(this.storageLocation);
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and there is nothing the
      // user can act on beyond retrying.
      this.loadError = true;
    } finally {
      this.isLoading = false;
    }
  }
}


